import type { AsyncState } from '../../../platform/async/Async'
import type { LocalDate } from '../../../platform/localDate'
import { monthName, monthSections } from '../../recurring/listSections'
import { currentOf } from '../../recurring/monthParam'
import type { Entry } from '../../recurring/recurringWire'

export const UPCOMING_ROWS = 5

export type UpcomingView = {
  /** "October" — the month `useMonth` was asked for (R10). */
  month: string
  /** M7's own section count (outflows only), not the length of what is shown. */
  overdueCount: number
  /** Overdue first, then upcoming, at most five. Selection and ordering only: no sum. */
  rows: { entry: Entry; overdue: boolean }[]
}

/** "October" — named here so the empty copy and the subtitle read one source. */
export const upcomingMonth = (today: LocalDate): string => monthName(currentOf(today))

/** The widget's whole input, derived through M7's `monthSections` so Overview cannot classify a bill differently. */
export function upcomingView(entries: Entry[], today: LocalDate): UpcomingView {
  const sections = monthSections(entries, today)
  const rows = [
    ...sections.overdue.entries.map((entry) => ({ entry, overdue: true })),
    ...sections.upcoming.entries.map((entry) => ({ entry, overdue: false })),
  ].slice(0, UPCOMING_ROWS)
  return { month: upcomingMonth(today), overdueCount: sections.overdue.count, rows }
}

/**
 * `useMonth`'s state as the widget's. Empty is decided here, so `<Async>` shows
 * "Nothing due" for a month with entries that are all paid or skipped. `stale`
 * cannot occur (`useMonth` passes no `asOf`) and is folded into success rather
 * than thrown at a live surface.
 */
export function toUpcomingState(state: AsyncState<Entry[]>, today: LocalDate): AsyncState<UpcomingView> {
  if (state.status !== 'success' && state.status !== 'stale') return state
  const view = upcomingView(state.data, today)
  return view.rows.length === 0 ? { status: 'empty' } : { status: 'success', data: view }
}
